import { campaignDimensions, campaignKeys } from '../../shared/funnel-analytics';
const capturedAtKey = 'campaign_captured_at';
const attributionLifetime = 30 * 24 * 60 * 60 * 1000;

interface CampaignClient {
  register(properties: Record<string, unknown>): void;
  unregister(key: string): void;
  get_property(key: string): unknown;
  has_opted_out_capturing(): boolean;
  capture(event: string, properties: Record<string, unknown>): unknown;
}

export function parseCampaign(search: string): Record<string, string> | null {
  const params = new URLSearchParams(search);
  const properties: Record<string, string> = {};
  for (const key of campaignKeys) {
    const value = params.get(key);
    // Campaign labels only: never collect arbitrary URLs, email addresses, or query text.
    if (value && /^[a-zA-Z0-9_-]{1,100}$/.test(value)) properties[key] = value;
  }
  return properties.utm_source && properties.utm_campaign ? properties : null;
}

export function clearCampaign(client: CampaignClient): void {
  for (const key of [...Object.keys(campaignDimensions), ...campaignKeys, ...campaignKeys.map(k => `first_${k}`), ...campaignKeys.map(k => `session_${k}`), 'first_campaign_captured_at', 'session_campaign_seen_at', capturedAtKey]) client.unregister(key);
}

/** PostHog's own persistence keeps these properties alongside the anonymous identity through OAuth. */
export function prepareCampaign(client: CampaignClient, pathname: string, search: string, now = Date.now()): () => void {
  const capturedAt = client.get_property(capturedAtKey);
  if (typeof capturedAt !== 'number' || capturedAt > now || now - capturedAt >= attributionLifetime) clearCampaign(client);
  if (client.has_opted_out_capturing()) {
    clearCampaign(client);
    return () => {};
  }
  // Only the public ad destination can start a campaign; never inspect share/auth callback queries.
  const campaign = ['/', '/browser-check', '/freebuff', '/adminbar', '/adminbar/'].includes(pathname) ? parseCampaign(search) : null;
  if (campaign) {
    for (const key of campaignKeys) client.unregister(key);
    if (!client.get_property('first_utm_campaign')) client.register({ ...Object.fromEntries(Object.entries(campaign).map(([key,value]) => [`first_${key}`,value])), first_campaign_captured_at: now });
    for (const key of campaignKeys) client.unregister(`session_${key}`);
    client.register({ ...campaign, ...Object.fromEntries(Object.entries(campaign).map(([key,value]) => [`session_${key}`,value])), [capturedAtKey]: now, session_campaign_seen_at: now });
  }
  const sessionSeen = client.get_property('session_campaign_seen_at');
  if (typeof sessionSeen === 'number' && now - sessionSeen < 30 * 60 * 1000) client.register({ session_campaign_seen_at: now });
  else { for (const key of campaignKeys) client.unregister(`session_${key}`); client.unregister('session_campaign_seen_at'); }
  let tracked = false;
  return () => {
    if (!campaign || tracked) return;
    tracked = true;
    if (client.has_opted_out_capturing()) return;
    client.capture('ad_landing_viewed', { ...campaign, surface: 'web' });
  };
}
